import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { LotusLogo } from '../components/Layout';
import { SectionHeader } from '../components/SectionHeader';
import { useLocations } from '../hooks/useLocations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, MapPin, Phone, Mail, Globe, Star, Plus, ExternalLink, Stethoscope, Send, HelpCircle, ThumbsUp, ChevronDown, ChevronRight, MessageSquare, Trash2, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QnASection } from '../components/shared/QnASection';


import { API } from '../lib/api';
// Sdíleno s detailem odborníka v Bloom Worldu, ať se to nerozejde.
import { mapaOdbornika as mapSrc, odkazDoMap, regionOdbornika,
         stitekKategorie as catLabel } from '../lib/odbornik';


const SpecialistsPage = () => {
  const { user, isAdmin } = useAuth();
  const { t, datum } = useJazyk();
  const [specialists, setSpecialists] = useState([]);
  const [specCats, setSpecCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [country, setCountry] = useState('CZ');
  const [selected, setSelected] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rr, setRr] = useState(0);
  const [rc, setRc] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const isSpecialistOrAdmin = isAdmin || user?.role === 'specialist';
  const { allLocations } = useLocations();
  const czLocations = allLocations.filter(l => l.country === 'CZ');
  const worldLocations = allLocations.filter(l => l.country === 'WORLD');
  const currentLocations = country === 'CZ' ? czLocations : worldLocations;

  useEffect(() => {
    axios.get(`${API}/specialist-categories`)
      .then(r => setSpecCats(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchSpecialists(); }, [catFilter, regionFilter, country]);

  const fetchSpecialists = async () => {
    setLoading(true);
    try {
      const p = { country };
      if (catFilter !== 'all') p.specialty = catFilter;
      if (regionFilter !== 'all') p.search = regionFilter;
      if (search) p.search = search;
      const r = await axios.get(`${API}/specialists`, { params: p });
      setSpecialists(r.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchReviews = async (id) => { try { const r = await axios.get(`${API}/specialists/${id}/reviews`); setReviews(r.data); } catch {} };
  const handleSelect = (s) => { setSelected(s); fetchReviews(s.id); };
  const handleSearch = (e) => { e.preventDefault(); fetchSpecialists(); };

  const handleReview = async (e) => {
    e.preventDefault(); if (!selected) return;
    const specialistId = selected.id;
    try {
      await axios.post(`${API}/specialists/${specialistId}/reviews`, { specialist_id: specialistId, rating: rr, content: rc });
      toast.success(t('odb.recenzePridana')); setReviewOpen(false); setRr(0); setRc('');
      // Refresh the open detail so the review count and rating update immediately
      try { const r = await axios.get(`${API}/specialists/${specialistId}`); setSelected(r.data); } catch {}
      fetchReviews(specialistId); fetchSpecialists();
    } catch { toast.error(t('odb.chybaRecenze')); }
  };


  const stars = (n) => [...Array(5)].map((_, i) =>
    <Star key={i} className={`w-3.5 h-3.5 ${i < n ? 'text-bloom-pride-pink fill-bloom-pride-pink' : 'text-border'}`} />
  );

  const SpecForm = ({ isSubmit }) => {
    const firstCat = specCats[0]?.name || '';
    const [f, setF] = useState({ name: '', specialty: firstCat, description: '', subcategory: '', address: '', city: '', region: '', country: 'CZ', phone: '', email: '', website: '' });
    const formLocs = f.country === 'WORLD' ? worldLocations : czLocations;

    useEffect(() => {
      if (specCats.length > 0 && !f.specialty) setF(p => ({ ...p, specialty: specCats[0].name }));
    }, [specCats]);

    const submitForm = async (e) => {
      e.preventDefault();
      if (isSubmit) {
        setSubmitLoading(true);
        try { await axios.post(`${API}/specialists/submit`, f); toast.success(t('odb.odeslanKeSchvaleni')); setSubmitDialogOpen(false); }
        catch { toast.error(t('odb.chybaOdeslat')); }
        finally { setSubmitLoading(false); }
      } else {
        try { await axios.post(`${API}/specialists`, f); toast.success(t('odb.pridan')); setAddDialogOpen(false); fetchSpecialists(); }
        catch { toast.error(t('odb.chybaPridat')); }
      }
    };
    return (
      <form onSubmit={submitForm} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-bloom-text text-sm">{t('odb.jmeno')}</Label><Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} required data-testid="spec-name-input" /></div>
          <div><Label className="text-bloom-text text-sm">{t('odb.kategorie')}</Label>
            <Select value={f.specialty} onValueChange={v => setF(p => ({ ...p, specialty: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{specCats.map(o => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label className="text-bloom-text text-sm">{t('odb.popis')}</Label>
          <Textarea value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} rows={2} placeholder={t('odb.popisPriklad')} data-testid="spec-desc-input" />
        </div>
        <div><Label className="text-bloom-text text-sm">{t('odb.adresa')}</Label><Input value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} required data-testid="spec-address-input" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-bloom-text text-sm">{t('odb.mesto')}</Label><Input value={f.city} onChange={e => setF(p => ({ ...p, city: e.target.value }))} required data-testid="spec-city-input" /></div>
          <div><Label className="text-bloom-text text-sm">{t('odb.kraj')}</Label>
            <Select value={f.region || 'none'} onValueChange={v => setF(p => ({ ...p, region: v === 'none' ? '' : v }))}>
              <SelectTrigger><SelectValue placeholder={t('odb.vyber')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {formLocs.map(o => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-bloom-text text-sm">{t('odb.stat')}</Label>
            <Select value={f.country} onValueChange={v => setF(p => ({ ...p, country: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CZ">{t('odb.cesko')}</SelectItem>
                <SelectItem value="WORLD">{t('pod.svet')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-bloom-text text-sm">{t('odb.telefon')}</Label><Input value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} data-testid="spec-phone-input" /></div>
          <div><Label className="text-bloom-text text-sm">E-mail</Label><Input type="email" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} data-testid="spec-email-input" /></div>
        </div>
        <div><Label className="text-bloom-text text-sm">{t('odb.web')}</Label><Input value={f.website} onChange={e => setF(p => ({ ...p, website: e.target.value }))} placeholder="https://..." data-testid="spec-web-input" /></div>
        <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={submitLoading} data-testid="spec-submit-btn">
          {isSubmit ? t(submitLoading ? 'odb.odesilam' : 'odb.odeslatKeSchvaleni') : t('odb.pridatOdbornika')}
        </Button>
      </form>
    );
  };

  // Sort state: 'default' | 'rating_desc' | 'rating_asc'
  const [sortOrder, setSortOrder] = useState('default');

  const sortedSpecialists = React.useMemo(() => {
    if (sortOrder === 'rating_desc') return [...specialists].sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
    if (sortOrder === 'rating_asc') return [...specialists].sort((a, b) => (a.avg_rating || 0) - (b.avg_rating || 0));
    return specialists;
  }, [specialists, sortOrder]);

  const regionOpts = [
    { id: 'all', name: t(country === 'CZ' ? 'pod.celaCR' : 'pod.celySvet') },
    ...currentLocations
  ];

  return (
    <div className="min-h-screen py-6" data-testid="specialists-page">
      <div className="pride-bar mb-6" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          sectionKey="specialists"
          klicTitulku="nav.specialists"
          klicPodtitulku="odb.podtitulek"
          defaultColor="#5BC0FF"
        />

        <div className="space-y-3 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" /><Input placeholder={t('odb.hledatPriklad')} value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="specialists-search-input" /></div>
            <Button type="submit" className="bg-bloom-violet text-white hover:bg-bloom-violet/90">{t('obecne.hledat')}</Button>
          </form>

          {/* Category filter */}
          <div className="flex flex-wrap gap-1.5">
            <button
              key="all"
              onClick={() => setCatFilter('all')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${catFilter === 'all' ? 'bg-bloom-violet text-white shadow-sm' : 'bg-white border border-border text-bloom-sub hover:border-bloom-violet/50 hover:text-bloom-violet'}`}
              data-testid="specialty-all"
            >
              <Stethoscope className="w-3.5 h-3.5" />{t('odb.vsechnyObory')}
            </button>
            {specCats.map(c => (
              <button key={c.id} onClick={() => setCatFilter(c.name)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${catFilter === c.name ? 'bg-bloom-violet text-white shadow-sm' : 'bg-white border border-border text-bloom-sub hover:border-bloom-violet/50 hover:text-bloom-violet'}`}
                data-testid={`specialty-${c.id}`}>
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            {/* Country toggle — CZ / Svět */}
            <div className="flex rounded-lg border border-border overflow-hidden">
              {[
                { value: 'CZ', label: 'pod.cesko' },
                { value: 'WORLD', label: 'pod.svet' },
              ].map(v => (
                <button key={v.value} onClick={() => { setCountry(v.value); setRegionFilter('all'); }}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${country === v.value ? 'bg-bloom-violet text-white' : 'bg-white text-bloom-sub hover:bg-muted'}`}
                  data-testid={`country-filter-${v.value}`}>
                  {t(v.label)}
                </button>
              ))}
            </div>
            {/* Region filter — for CZ and WORLD, from API */}
            {currentLocations.length > 0 && (
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-48" data-testid="region-filter"><MapPin className="w-4 h-4 mr-1 text-bloom-violet" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regionOpts.map(o => <SelectItem key={o.id} value={o.name || 'all'}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {/* Admin: direct add */}
            {isAdmin && (
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild><Button className="bg-bloom-mint text-white hover:bg-bloom-mint/90" data-testid="add-specialist-btn"><Plus className="w-4 h-4 mr-1" />{t('pod.pridat')}</Button></DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-bloom-text">{t('odb.pridatOdbornika')}</DialogTitle>
                    <DialogDescription>{t('odb.pridejteOdbornika')}</DialogDescription>
                  </DialogHeader>
                  <SpecForm isSubmit={false} />
                </DialogContent>
              </Dialog>
            )}
            {/* Regular user: suggest specialist */}
            {!isAdmin && (user?.email_verified ? (
              <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
                <DialogTrigger asChild><Button variant="outline" className="border-bloom-violet/30 text-bloom-violet" data-testid="suggest-specialist-btn"><Send className="w-4 h-4 mr-1" />{t('odb.navrhnout')}</Button></DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-bloom-text">{t('odb.navrhnout')}</DialogTitle>
                    <DialogDescription>{t('odb.navrhnetePopis')}</DialogDescription>
                  </DialogHeader>
                  <div className="text-xs text-bloom-sub bg-muted/50 rounded-lg p-3 mb-3">
                    {t('odb.navrhVysvetleni')}
                  </div>
                  <SpecForm isSubmit={true} />
                </DialogContent>
              </Dialog>
            ) : (
              <Link to="/profile" className="text-sm text-amber-600 hover:text-amber-700 font-medium">{t('odb.overEmail')}</Link>
            ))}
          </div>

          {/* Sort buttons — below obor + lokalita filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-bloom-sub font-medium">{t('odb.radit')}</span>
            {[
              { key: 'default', label: 'odb.vychozi' },
              { key: 'rating_desc', label: 'odb.nejvyssiHodnoceni' },
              { key: 'rating_asc', label: 'odb.nejnizsiHodnoceni' },
            ].map(o => (
              <button key={o.key} onClick={() => setSortOrder(o.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${sortOrder === o.key ? 'bg-bloom-mint text-white border-bloom-mint shadow-sm' : 'bg-white border-border text-bloom-sub hover:border-bloom-mint/50 hover:text-bloom-mint'}`}
                data-testid={`sort-${o.key}`}
              >{t(o.label)}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> :
              sortedSpecialists.length === 0 ? (
                <Card className="bg-white border-border/50"><CardContent className="p-8 text-center"><Stethoscope className="w-10 h-10 text-bloom-sub/30 mx-auto mb-3" /><p className="text-bloom-sub">{t('odb.zadniNalezeni')}</p><p className="text-xs text-bloom-sub mt-1">{t('odb.zkusteFiltry')}</p></CardContent></Card>
              ) : sortedSpecialists.map(s => (
                <Card key={s.id} className={`bg-white border-border/50 cursor-pointer transition-all ${selected?.id === s.id ? 'ring-2 ring-bloom-violet' : 'hover:shadow-md'}`} onClick={() => handleSelect(s)} data-testid={`specialist-card-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-bloom-text text-sm">{s.name}</h3>
                        <p className="text-xs text-bloom-violet mt-0.5 truncate">{catLabel(s.specialty, specCats)}</p>
                        <p className="text-xs text-bloom-sub flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{s.city}{s.region ? `, ${s.region}` : ''}</p>
                        {s.assigned_locations?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {s.assigned_locations.slice(0, 2).map(l => (
                              <span key={l} className="text-[10px] px-1.5 py-0.5 rounded-full bg-bloom-violet/10 text-bloom-violet">{l}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex">{stars(Math.round(s.avg_rating))}</div>
                        <p className="text-[11px] text-bloom-sub mt-0.5">{s.review_count} {t('odb.recenzi', { pocet: s.review_count })}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>

          <div className="lg:col-span-3 space-y-4">
            {selected ? (
              <>
                {mapSrc(selected) && (
                  <div className="rounded-xl overflow-hidden border border-border aspect-video bg-muted">
                    <iframe title="Mapa" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                      src={mapSrc(selected)}
                      data-testid="specialist-map" />
                  </div>
                )}
                <Card className="bg-white border-border/50"><CardContent className="p-5">
                  <h2 className="font-serif text-xl font-bold text-bloom-text mb-0.5">{selected.name}</h2>
                  <p className="text-bloom-violet text-sm font-medium mb-2">{catLabel(selected.specialty, specCats)}</p>
                  {selected.description && <p className="text-sm text-bloom-sub mb-3 italic">{selected.description}</p>}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-bloom-violet mt-0.5" /><div><p className="text-bloom-text">{selected.address}</p><p className="text-bloom-sub">{selected.city}{regionOdbornika(selected) ? `, ${regionOdbornika(selected)}` : ''}</p></div></div>
                    {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-bloom-violet" /><a href={`tel:${selected.phone}`} className="text-bloom-text hover:text-bloom-violet">{selected.phone}</a></div>}
                    {selected.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-bloom-violet" /><a href={`mailto:${selected.email}`} className="text-bloom-text hover:text-bloom-violet">{selected.email}</a></div>}
                    {selected.website && <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-bloom-violet" /><a href={selected.website} target="_blank" rel="noopener noreferrer" className="text-bloom-text hover:text-bloom-violet flex items-center gap-1">Web<ExternalLink className="w-3 h-3" /></a></div>}
                  </div>
                  {selected.assigned_locations?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-bloom-sub mb-1.5">{t('odb.dostupnyV')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selected.assigned_locations.map(l => (
                          <span key={l} className="px-2 py-0.5 rounded-full bg-bloom-violet/10 text-bloom-violet text-xs">{l}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <div className="flex">{stars(Math.round(selected.avg_rating))}</div>
                    <span className="text-xs text-bloom-sub">({selected.review_count} {t('odb.recenzi', { pocet: selected.review_count })})</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <a href={odkazDoMap(selected)} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full text-bloom-violet border-bloom-violet/30"><MapPin className="w-4 h-4 mr-1" />{t('odb.otevritVMapach')}</Button>
                    </a>
                    {user?.email_verified && (
                    <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                      <DialogTrigger asChild><Button className="flex-1 bg-bloom-violet text-white"><Star className="w-4 h-4 mr-1" />{t('odb.pridatRecenzi')}</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-serif text-bloom-text">{t('odb.pridatRecenzi')}</DialogTitle>
                          <DialogDescription>{t('odb.ohodnotte')}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleReview} className="space-y-4">
                          <div><Label className="text-bloom-text">{t('odb.vaseHodnoceni')}</Label>
                            <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(r => <button key={r} type="button" onClick={() => setRr(r)} className="p-0.5"><Star className={`w-7 h-7 ${r <= rr ? 'text-bloom-pride-pink fill-bloom-pride-pink' : 'text-border'}`} /></button>)}</div>
                          </div>
                          <div><Label className="text-bloom-text">{t('odb.popisZkusenosti')}</Label>
                            <Textarea value={rc} onChange={e => setRc(e.target.value)} rows={4} required placeholder={t('odb.popisteZkusenost')} />
                          </div>
                          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={rr === 0}>{t('odb.odeslatRecenzi')}</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                    )}
                  </div>
                </CardContent></Card>
                <div className="space-y-3">
                  <h3 className="font-serif text-lg font-semibold text-bloom-text">{t('odb.recenze', { pocet: reviews.length })}</h3>
                  {reviews.length === 0 ? <p className="text-bloom-sub text-sm text-center py-6">{t('odb.zadneRecenze')}</p> :
                    reviews.map(r => (
                      <Card key={r.id} className="bg-white border-border/50"><CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2"><span className="font-medium text-bloom-text text-sm">{r.username}</span><div className="flex">{stars(r.rating)}</div></div>
                        <p className="text-sm text-bloom-sub">{r.content}</p>
                        <p className="text-[11px] text-bloom-sub mt-2">{datum(r.created_at)}</p>
                      </CardContent></Card>
                    ))}
                </div>
              </>
            ) : (
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center border border-border">
                <div className="text-center"><LotusLogo size={48} /><p className="text-bloom-sub mt-3 text-sm">{t('odb.vyberteOdbornika')}</p></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Specialists Q&A Section */}
      <div className="max-w-7xl mx-auto px-4 mt-12 mb-16">
        <QnASection
          section="specialists"
          canAnswer={isSpecialistOrAdmin}
          canDelete={isSpecialistOrAdmin}
          showVoteCount={false}
          accentColor="pink"
          testIdPrefix="spec-question"
          addButtonLabel={t('odb.polozitOtazku')}
          dialogTitle={t('odb.novaOtazka')}
          dialogPlaceholder={t('odb.naCoSeZeptat')}
          user={user}
          categories={specCats.map(c => ({ id: c.name, name: c.name }))}
          activeCategory={catFilter}
        />
      </div>
    </div>
  );
};

export default SpecialistsPage;
